import { type ReactNode } from 'react';
import { Search } from 'lucide-react';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import { cn } from '@/lib/utils';

export const marketingFilterInputClass =
  'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

export const marketingFilterSelectClass = 'w-full min-w-[10.5rem] sm:w-44 shrink-0';

export { SearchableSelect as MarketingFilterSelect };

type MarketingFilterPanelProps = {
  children: ReactNode;
  summary?: string;
  onClear?: () => void;
  showClear?: boolean;
  className?: string;
};

export function MarketingFilterPanel({
  children,
  summary,
  onClear,
  showClear,
  className,
}: MarketingFilterPanelProps) {
  return (
    <div className={cn('mb-4 rounded-lg border border-gray-200 bg-white p-3', className)}>
      <div className="flex flex-wrap gap-2">{children}</div>
      {(summary || showClear) && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
          {summary ? <span>{summary}</span> : <span />}
          {showClear && onClear && (
            <button
              type="button"
              onClick={onClear}
              className="font-medium text-blue-700 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type MarketingFilterSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
};

export function MarketingFilterSearch({
  value,
  onChange,
  placeholder = 'Search…',
  className,
  'aria-label': ariaLabel,
}: MarketingFilterSearchProps) {
  return (
    <div className={cn('relative min-w-[12rem] flex-1', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className={cn(marketingFilterInputClass, 'w-full pl-8')}
      />
    </div>
  );
}

export function marketingMatchSummary(
  filtered: number,
  total: number,
  noun: string,
  loading = false,
): string {
  if (loading) return 'Loading…';
  if (filtered !== total) {
    return `${filtered} match${filtered === 1 ? '' : 'es'} of ${total}`;
  }
  return `${total} ${noun}${total === 1 ? '' : 's'}`;
}
